/**
 * Header/Navigation Component
 *
 * Main navigation bar shown on protected pages.
 * Includes branding, API credits, and user menu.
 */

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import FeedbackModal from '../FeedbackModal';
import '../../styles/Header.css';

export default function Header() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const userTier = profile?.subscription_tier ?? 'free';
  const isTrialing = profile?.subscription_status === 'trialing';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleLogout = async () => {
    try {
      setIsMenuOpen(false); // Close menu immediately
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      if (window.location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          <Link to="/dashboard" className="header-brand">
            <img src="/logo.png" alt="Arber Logo" className="header-logo" />
            <h1>rber</h1>
          </Link>
        </div>

        <div className="header-right">


          <button
            className="header-feedback-btn"
            onClick={() => setIsFeedbackOpen(true)}
            title="Send feedback"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            Feedback
          </button>

          <div className="user-menu" ref={menuRef}>
            <button
              className={`user-menu-trigger user-menu-trigger--${userTier}`}
              onClick={toggleMenu}
              aria-expanded={isMenuOpen}
              aria-haspopup="true"
            >
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className={`user-avatar user-avatar--${userTier}`}
                />
              ) : (
                <div className={`user-avatar-placeholder user-avatar-placeholder--${userTier}`}>
                  {user?.email?.[0].toUpperCase() || 'U'}
                </div>
              )}
            </button>
            {isMenuOpen && (
              <div className="user-menu-dropdown">
                <div className="user-menu-header">
                  <div className="user-menu-email">{user?.email}</div>
                  <div className={`user-menu-tier user-menu-tier--${userTier}`}>
                    <span className="user-menu-tier__icon">
                      {userTier === 'free' && '⭐'}
                      {userTier === 'gold' && '👑'}
                      {userTier === 'platinum' && '💎'}
                    </span>
                    <span className="user-menu-tier__name">
                      {userTier.charAt(0).toUpperCase() + userTier.slice(1)}
                      {isTrialing && ' Trial'}
                    </span>
                  </div>
                </div>
                <div className="user-menu-divider" />
                <Link to="/dashboard" className="user-menu-item" onClick={() => setIsMenuOpen(false)}>
                  Dashboard
                </Link>
                <Link to="/account" className="user-menu-item" onClick={() => setIsMenuOpen(false)}>
                  Account Settings
                </Link>
                <Link to="/account/billing" className="user-menu-item" onClick={() => setIsMenuOpen(false)}>
                  Billing
                </Link>
                {user?.email === 'arberodds@gmail.com' && (
                  <>
                    <div className="user-menu-divider" />
                    <Link to="/admin/feedback" className="user-menu-item" onClick={() => setIsMenuOpen(false)}>
                      Admin: Feedback
                    </Link>
                  </>
                )}
                <div className="user-menu-divider" />
                <button onClick={handleLogout} className="user-menu-item user-menu-logout">
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </header>
  );
}
